import * as React from "react";
import { useMemo, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useSoundContext } from "./Hooks/useGameSound";

const pulse = keyframes`
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
    }
`;

const show = keyframes`
	0% {
		opacity: 0;
	}
	100% {
		opacity: 100;
    }
`;

const hide = keyframes`
	0% {
        opacity: 1;
        width: 100%;
        height: 100%;
	}
	99% {
        opacity: 0;
        width: 100%;
        height: 100%;
    }
	100% {
        opacity: 0;
        width: 0%;
        height: 0%;
    }
`;

const scaleIn = keyframes`
	0% {
		transform: scale(0);
	}
	100% {
		transform: scale(1);
    }
`;

interface Props {
  playedCards: { pileIndex: number; playedCard: number }[];
}

const BurstAnimation = styled.div<{
  animationDelay: number;
  animationSpeed: number;
}>`
  width: 16rem;
  height: 16rem;
  position: absolute;
  top: 0px;
  animation-name: ${pulse};
  animation-fill-mode: infinite;

  ${({ animationDelay = 0 }) =>
    animationDelay &&
    `animation-delay: ${animationDelay}s; 
    `};

  ${({ animationSpeed = 0 }) =>
    animationSpeed &&
    `animation-duration: ${animationSpeed}s; 
    `};
`;

const Burst = styled.div<{
  background: string;
}>`
  background: ${({ background }) => background};
  width: 16rem;
  height: 16rem;
  position: relative;
  top: 0px;
  text-align: center;
  animation: ${scaleIn} 1s linear forwards;

  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 16rem;
    height: 16rem;
    background: ${({ background }) => background};
  }
  &:before {
    transform: rotate(30deg);
  }
  &:after {
    transform: rotate(60deg);
  }
`;

const Number = styled.div`
  font-size: 4rem;
  font-weight: bold;
  opacity: 0;
  animation: ${show} 1s linear forwards;
  animation-delay: 1.4s;
`;

const Heading = styled.div`
  font-size: 2rem;
  opacity: 0;
  animation: ${show} 1s linear forwards;
  animation-delay: 1.2s;
`;

const Content = styled.div`
  position: relative;
  height: 16rem;
  width: 16rem;
  opacity: 0;
  animation: ${show} 1s linear forwards;
  animation-delay: 0.5s;
`;

const BurstContent = styled.div`
  position: absolute;
  height: 16rem;
  width: 16rem;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0px;
  flex-direction: column;
`;

const StyledMagic10Animation = styled.div<{
  isMagic: boolean;
}>`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  opacity: 0;
  animation: ${show} 0.5s linear forwards, ${hide} 0.3s linear 3s forwards;
`;

const Magic10Animation = ({ playedCards }: Props) => {
  const [isVisible, setIsVisible] = React.useState(true);
  const { playSound } = useSoundContext();
  const lastCard = playedCards[playedCards.length - 1];
  const cardsInTheLastPlayedPile = playedCards.filter(
    (p) => p.pileIndex === lastCard.pileIndex
  );
  const secondLastCardInTheLastPile =
    cardsInTheLastPlayedPile[cardsInTheLastPlayedPile.length - 2];

  const lastNumber = lastCard ? lastCard.playedCard : 100;
  const showAnimation = useMemo(() => {
    if (lastCard && secondLastCardInTheLastPile) {
      if (
        (lastCard.pileIndex <= 2 &&
          lastCard.playedCard - secondLastCardInTheLastPile.playedCard ===
            -10) ||
        (lastCard.pileIndex >= 3 &&
          lastCard.playedCard - secondLastCardInTheLastPile.playedCard === 10)
      ) {
        return true;
      }
    }
    return false;
  }, [lastCard, secondLastCardInTheLastPile]);
  useEffect(() => {
    if (showAnimation && isVisible) {
      playSound("magic");
    }
  }, [playSound, showAnimation, isVisible]);

  if (showAnimation && isVisible) {
    return (
      <StyledMagic10Animation
        key={lastNumber}
        isMagic={true}
        onClick={() => setIsVisible(false)}
      >
        <Content>
          <BurstAnimation
            key={"burst-1"}
            animationDelay={0.5}
            animationSpeed={2}
          >
            <Burst background="black"></Burst>
          </BurstAnimation>
          <BurstAnimation
            key={"burst-2"}
            animationDelay={1.0}
            animationSpeed={3}
          >
            <Burst background="#b5b58f"></Burst>
          </BurstAnimation>
          <BurstContent>
            <Heading>
              magic!
              <span role="img" aria-label="Confetti emoji">
                🎉
              </span>
            </Heading>
            <Number>{lastNumber}</Number>
          </BurstContent>
        </Content>
      </StyledMagic10Animation>
    );
  }

  return <div />;
};

export default Magic10Animation;
